import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
/**
 * ProductCard
 * Props:
 *  - product: { id, name, price, originalPrice, image, category, rating, reviews, badge, inStock }
 *  - onAddToCart: (product) => void
 *  - style: inline style (used for staggered animation delay)
 */
export default function ProductCard({ product, onAddToCart, style }) {
  const [added, setAdded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const Navigate = useNavigate()
  const { addItem } = useCart();

  // Guards against rapid double-clicks firing two add-to-cart requests.
  // A ref is used (not state) because state updates are async — two
  // clicks within the same render cycle would both see the old value.
  const isAddingRef = useRef(false);

  const {
    _id,
    name = "Unnamed Product",
    price = 0,
    originalPrice,
    image,
    category = "",
    rating = 0,
    reviewsCount = 0,
    badge,
    inStock = false,
    stockQuantity = 0,
    emoji = "🌿",
  } = product;

  const imageUrl = image?.url || null;

  const isAvailable = inStock && stockQuantity > 0;
  const discount =
    originalPrice && originalPrice > price
      ? Math.round(((originalPrice - price) / originalPrice) * 100)
      : null;


  const handleAdd = () => {
    if (!isAvailable || isAddingRef.current) return;

    isAddingRef.current = true;
    setAdded(true);
    onAddToCart?.(product);

    setTimeout(() => {
      setAdded(false);
      isAddingRef.current = false;
    }, 2200);
  };
  const Stars = ({ value }) =>
    [1, 2, 3, 4, 5].map((s) => (
      <span
        key={s}
        className="pc-star"
        style={{ color: s <= Math.round(value) ? "#d4a017" : "var(--c-border)" }}
      >★</span>
    ));

  const handleCardClick = (p) => {
    Navigate(`/product/${p._id}`, {
      state: p,
    });
  }

  return (
    <article className={`pc ${!isAvailable ? "pc--oos" : ""}`} style={style}
      onClick={() => { handleCardClick(product) }}>
      {/* Badge */}
      {badge && <span className="pc-badge">{badge}</span>}
      {discount && <span className="pc-discount">-{discount}%</span>}

      {/* Image / Fallback */}
      <div className="pc-img-wrap">
        {image && !imgError ? (
          <img
            src={imageUrl}
            alt={name}
            className="pc-img"
            onError={() => setImgError(true)}
            loading="lazy"

          />
        ) : (
          <div className="pc-img-fallback">
            <span>{emoji}</span>
          </div>
        )}
        {!isAvailable && <div className="pc-oos-overlay">Out of Stock</div>}
      </div>

      {/* Body */}
      <div className="pc-body">
        {category && <span className="pc-category">{category}</span>}
        <h3 className="pc-name">{name}</h3>

        {/* Price */}
        <div className="pc-price-row">
          <span className="pc-price">₹{price.toLocaleString("en-IN")}</span>
          {originalPrice && (
            <span className="pc-original">₹{originalPrice.toLocaleString("en-IN")}</span>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="pc-footer">
        <button
          className={`pc-btn ${added ? "pc-btn--added" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            handleAdd();
          }}
          disabled={!isAvailable || added}
          aria-label={`Add ${name} to cart`}
        >
          {added
            ? "✓ Added!"
            : isAvailable
              ? "Add To Cart"
              : "Unavailable"}
        </button>
      </div>
    </article>
  );
}